import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Eye, EyeOff, ArrowRight, BarChart2, Shield, Zap, CheckCircle, AlertCircle, X } from 'lucide-react';
import { API_URL } from '../styles/shared';
import MarketPulzeLogo from '../components/MarketPulzeLogo';

// ─── Resend Verification Button ──────────────────────────────────────────────
const ResendVerificationButton = ({ email, apiUrl }) => {
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [cooldown, setCooldown] = useState(0);

  const handleResend = async () => {
    if (!email) { setStatus('error'); return; }
    setStatus('sending');
    try {
      await fetch(`${apiUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setStatus('sent');
      setCooldown(60);
      const timer = setInterval(() => {
        setCooldown(c => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
      }, 1000);
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') return (
    <p style={{ fontSize: '0.8rem', color: '#00ff88', margin: 0, paddingLeft: '1.6rem' }}>
      ✅ Verification email sent! Check your inbox. {cooldown > 0 && `(resend in ${cooldown}s)`}
    </p>
  );

  return (
    <button type="button" onClick={handleResend} disabled={status === 'sending' || cooldown > 0}
      style={{ marginLeft: '1.6rem', padding: '0.4rem 1rem', background: 'rgba(255,170,0,0.12)', border: '1px solid rgba(255,170,0,0.3)', borderRadius: '6px', color: '#ffaa00', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
      {status === 'sending' ? '⏳ Sending...' : '📧 Resend verification email'}
    </button>
  );
};

// ─── Password Strength Utility ───────────────────────────────────────────────
const getPasswordStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Weak', color: '#ff4444' };
  if (score <= 2) return { score, label: 'Fair', color: '#ffaa00' };
  if (score <= 3) return { score, label: 'Good', color: '#00d4ff' };
  return { score, label: 'Strong', color: '#00ff88' };
};

// ─── Animated Ticker ─────────────────────────────────────────────────────────
const tickers = [
  { sym: 'AAPL', price: '227.52', chg: '+1.23%', up: true },
  { sym: 'NVDA', price: '875.40', chg: '+3.87%', up: true },
  { sym: 'TSLA', price: '192.36', chg: '-2.14%', up: false },
  { sym: 'MSFT', price: '415.20', chg: '+0.92%', up: true },
  { sym: 'AMZN', price: '198.74', chg: '+1.56%', up: true },
  { sym: 'GOOGL', price: '175.98', chg: '-0.33%', up: false },
  { sym: 'META', price: '562.10', chg: '+2.41%', up: true },
  { sym: 'SPY', price: '568.34', chg: '+0.72%', up: true },
];

// ─── Field Component ──────────────────────────────────────────────────────────
const Field = ({ label, type, value, onChange, placeholder, error, hint, children }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', fontWeight: 600, color: focused ? '#00d4ff' : '#94a3b8', marginBottom: '0.45rem', letterSpacing: '0.04em', transition: 'color 0.2s' }}>
        <span>{label}</span>
        {hint && <span style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 400 }}>{hint}</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoComplete={type === 'password' ? 'new-password' : 'email'}
          style={{
            width: '100%', padding: '0.85rem 1rem', paddingRight: children ? '3rem' : '1rem',
            background: focused ? 'rgba(0,212,255,0.04)' : 'rgba(10,20,45,0.6)',
            border: `1.5px solid ${error ? '#ff4444' : focused ? '#00d4ff' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '10px', color: '#f8fafc', fontSize: '0.95rem', outline: 'none',
            boxSizing: 'border-box', transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        />
        {children}
      </div>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
          <AlertCircle size={12} style={{ color: '#ff4444', flexShrink: 0 }} />
          <span style={{ fontSize: '0.75rem', color: '#ff4444' }}>{error}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main AuthPage ────────────────────────────────────────────────────────────
const ForgotPasswordForm = ({ email, setEmail, apiUrl, onBack }) => {
  const [sent,    setSent]    = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error,   setError]   = React.useState('');
  const [localEmail, setLocalEmail] = React.useState(email || '');

  const handleSubmit = async () => {
    if (!localEmail.includes('@')) { setError('Please enter a valid email address.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(apiUrl + '/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: localEmail }),
      });
      const d = await res.json();
      if (d.success || res.ok) setSent(true);
      else setError(d.message || 'Something went wrong. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📧</div>
      <h3 style={{ color: '#00ff88', fontWeight: 800, marginBottom: '0.5rem' }}>Check your email</h3>
      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        If an account exists for <strong style={{ color: '#e2e8f0' }}>{localEmail}</strong>, you'll receive a reset link shortly.
      </p>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit' }}>← Back to Sign In</button>
    </div>
  );

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      <h3 style={{ color: '#f8fafc', fontWeight: 800, marginBottom: '0.4rem' }}>Reset your password</h3>
      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Enter your email and we'll send you a link to reset your password.
      </p>
      {error && <div style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.25)', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.85rem', color: '#ff6b6b' }}>{error}</div>}
      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>EMAIL ADDRESS</label>
        <input
          type="email" value={localEmail}
          onChange={e => { setLocalEmail(e.target.value); setEmail(e.target.value); setError(''); }}
          placeholder="you@example.com"
          style={{ width: '100%', padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#f8fafc', fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
      </div>
      <button onClick={handleSubmit} disabled={loading} style={{
        width: '100%', padding: '0.9rem', background: loading ? 'rgba(0,255,136,0.3)' : 'linear-gradient(135deg,#00ff88,#00d4ff)',
        border: 'none', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.9rem',
        cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: '1rem'
      }}>
        {loading ? 'Sending…' : 'Send Reset Link'}
      </button>
      <div style={{ textAlign: 'center' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' }}>← Back to Sign In</button>
      </div>
    </div>
  );
};

const AuthPage = ({ initialMode = 'login', onSuccess, onClose, onNavigate, savedFormData, onFormSave }) => {
  const [mode, setMode] = useState(savedFormData?.mode || initialMode);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'verify-sent'

  // Form state - restored from savedFormData if available
  const [email, setEmail] = useState(savedFormData?.email || '');
  const [confirmEmail, setConfirmEmail] = useState(savedFormData?.confirmEmail || '');
  const [password, setPassword] = useState(savedFormData?.password || '');
  const [confirmPassword, setConfirmPassword] = useState(savedFormData?.confirmPassword || '');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreed, setAgreed] = useState(savedFormData?.agreed || false);

  // Errors
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');

  // Ticker animation
  const [tickerOffset, setTickerOffset] = useState(0);
  const tickerRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setTickerOffset(o => o - 1);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  const pwStrength = getPasswordStrength(password);

  const validate = () => {
    const e = {};
    if (!email) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address';

    if (mode === 'register') {
      if (!confirmEmail) e.confirmEmail = 'Please confirm your email';
      else if (email.toLowerCase() !== confirmEmail.toLowerCase()) e.confirmEmail = 'Email addresses do not match';
      if (!password) e.password = 'Password is required';
      else if (password.length < 8) e.password = 'Password must be at least 8 characters';
      if (!confirmPassword) e.confirmPassword = 'Please confirm your password';
      else if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match';
      if (!agreed) e.agreed = 'You must agree to the Terms of Service';
    } else {
      if (!password) e.password = 'Password is required';
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const res = await axios.post(`${API_URL}${endpoint}`, { email, password });
      if (res.data.success) {
        if (mode === 'register') {
          setStep('verify-sent');
        } else {
          onSuccess(res.data.token, res.data.user);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      if (msg.toLowerCase().includes('email') && msg.toLowerCase().includes('exist')) {
        setErrors({ email: 'An account with this email already exists' });
      } else if (msg.toLowerCase().includes('password') || msg.toLowerCase().includes('invalid')) {
        setErrors({ password: 'Incorrect password. Please try again.' });
      } else if (msg.toLowerCase().includes('verify')) {
        setGlobalError('Please verify your email before logging in. Check your inbox.');
      } else {
        setGlobalError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(m => m === 'login' ? 'register' : 'login');
    setErrors({});
    setGlobalError('');
    setPassword('');
    setConfirmPassword('');
    setConfirmEmail('');
    setAgreed(false);
    setStep('form');
  };

  // ── Verify Sent Screen ────────────────────────────────────────────────────
  if (step === 'verify-sent') {
    return (
      <div style={styles.pageWrap}>
        <div style={styles.verifyCard}>
          <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>📬</div>
          <h2 style={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem' }}>Check your inbox</h2>
          <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '0.5rem' }}>
            We sent a verification link to
          </p>
          <p style={{ color: '#00d4ff', fontWeight: 700, marginBottom: '1.5rem', wordBreak: 'break-all' }}>{email}</p>
          <div style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '2rem', textAlign: 'left' }}>
            {['Click the link in your email to verify your account', 'Check your spam/junk folder if you do not see it', 'The link expires in 24 hours'].map((tip, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: i < 2 ? '0.6rem' : 0 }}>
                <CheckCircle size={14} style={{ color: '#00ff88', flexShrink: 0 }} />
                <span style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>{tip}</span>
              </div>
            ))}
          </div>
          <button onClick={() => onClose && onClose()} style={styles.btnSecondary}>
            Back to MarketPulze
          </button>
          <p style={{ color: '#475569', fontSize: '0.8rem', marginTop: '1.25rem' }}>
            Wrong email? <button onClick={switchMode} style={styles.linkBtn}>Go back and try again</button>
          </p>
        </div>
      </div>
    );
  }

  // ── Form Screen ───────────────────────────────────────────────────────────
  return (
    <div style={styles.pageWrap}>
      {/* LEFT PANEL */}
      <div style={styles.leftPanel}>
        {/* Ticker tape */}
        <div style={styles.tickerWrap}>
          <div ref={tickerRef} style={{ display: 'flex', gap: '2.5rem', whiteSpace: 'nowrap', transform: `translateX(${tickerOffset % 600}px)`, transition: 'none' }}>
            {[...tickers, ...tickers].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.05em' }}>{t.sym}</span>
                <span style={{ fontSize: '0.75rem', color: '#f8fafc', fontWeight: 600 }}>{t.price}</span>
                <span style={{ fontSize: '0.7rem', color: t.up ? '#00ff88' : '#ff4444', fontWeight: 700 }}>{t.chg}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Branding */}
        <div style={{ padding: '3rem 3rem 2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '4rem' }}>
            <MarketPulzeLogo size={40} />
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 900, background: 'linear-gradient(135deg,#00ff88,#00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>MarketPulze</div>
              <div style={{ fontSize: '0.6rem', color: '#475569', letterSpacing: '0.1em' }}>AI STOCK INTELLIGENCE</div>
            </div>
          </div>

          <h1 style={{ fontSize: '2.75rem', fontWeight: 900, color: '#f8fafc', lineHeight: 1.1, marginBottom: '1.25rem', letterSpacing: '-0.03em' }}>
            Analyze any stock<br />
            <span style={{ background: 'linear-gradient(135deg,#00ff88,#00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>in seconds.</span>
          </h1>
          <p style={{ fontSize: '1rem', color: '#64748b', lineHeight: 1.7, marginBottom: '3rem', maxWidth: '380px' }}>
            Algorithmic stock screening for US equities. 15-minute delayed data. Invite-only beta.
          </p>

          {/* Value props */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { icon: <BarChart2 size={18} style={{ color: '#00d4ff' }} />, title: 'Algorithmic Screening', sub: 'US equities — any ticker' },
              { icon: <Zap size={18} style={{ color: '#00ff88' }} />, title: 'BULLISH / BEARISH / NEUTRAL', sub: 'Algorithmic signals — not investment advice' },
              { icon: <Shield size={18} style={{ color: '#ffaa00' }} />, title: 'Invite-Only Beta', sub: 'Free for invited members' },
            ].map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9rem', padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ padding: '0.4rem', background: 'rgba(0,212,255,0.08)', borderRadius: '8px', flexShrink: 0 }}>{p.icon}</div>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc', marginBottom: '0.1rem' }}>{p.title}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{p.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Competitor comparison */}
          <div style={{ marginTop: '2.5rem', padding: '1rem', background: 'rgba(0,255,136,0.04)', border: '1px solid rgba(0,255,136,0.1)', borderRadius: '10px' }}>
            <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '0 0 0.6rem', fontWeight: 600, letterSpacing: '0.05em' }}>COMPARED TO ALTERNATIVES</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {[['Guest Preview', 'Limited'], ['Full Analysis', 'Invited members only'], ['MarketPulze', 'Free beta']].map(([name, price], i) => (
                <div key={i} style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#64748b' }}>{name} </span>
                  <span style={{ color: i === 2 ? '#00ff88' : '#ff6b6b', fontWeight: 700, textDecoration: i < 2 ? 'line-through' : 'none' }}>{price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div style={styles.rightPanel}>
        {onClose && (
          <button onClick={onClose} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: '0.3rem' }}>
            <X size={20} />
          </button>
        )}

        <div style={{ maxWidth: '420px', width: '100%' }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', background: 'rgba(10,20,45,0.8)', borderRadius: '12px', padding: '0.3rem', marginBottom: '2.25rem', border: '1px solid rgba(255,255,255,0.06)' }}>
            {['login'].map(m => (
              <button key={m} onClick={() => { setMode(m); setErrors({}); setGlobalError(''); }} style={{
                flex: 1, padding: '0.65rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.875rem', transition: 'all 0.2s', fontFamily: 'inherit',
                background: mode === m ? 'linear-gradient(135deg,rgba(0,255,136,0.15),rgba(0,212,255,0.15))' : 'transparent',
                color: mode === m ? '#00d4ff' : '#475569',
                boxShadow: mode === m ? '0 0 0 1px rgba(0,212,255,0.2)' : 'none',
              }}>
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          <h2 style={{ fontSize: '1.6rem', fontWeight: 900, color: '#f8fafc', marginBottom: '0.4rem', letterSpacing: '-0.02em' }}>
            {mode === 'login' ? 'Welcome back' : 'Start for free'}
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '2rem' }}>
            {mode === 'login' ? 'Sign in to access your AI analysis dashboard' : 'Sign in to access your full analysis dashboard'}
          </p>

          {globalError && (
            <div style={{ background: globalError.includes('verify') ? 'rgba(255,170,0,0.08)' : 'rgba(255,68,68,0.08)', border: `1px solid ${globalError.includes('verify') ? 'rgba(255,170,0,0.25)' : 'rgba(255,68,68,0.25)'}`, borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: globalError.includes('verify') ? '0.75rem' : 0 }}>
                <AlertCircle size={16} style={{ color: globalError.includes('verify') ? '#ffaa00' : '#ff4444', flexShrink: 0, marginTop: '1px' }} />
                <span style={{ fontSize: '0.85rem', color: globalError.includes('verify') ? '#ffaa00' : '#ff6b6b' }}>{globalError}</span>
              </div>
              {globalError.includes('verify') && (
                <ResendVerificationButton email={email} apiUrl={API_URL} />
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Field label="EMAIL ADDRESS" type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: '' })); }} placeholder="you@example.com" error={errors.email} />

            {mode === 'register' && (
              <Field label="CONFIRM EMAIL" type="email" value={confirmEmail} onChange={e => { setConfirmEmail(e.target.value); setErrors(prev => ({ ...prev, confirmEmail: '' })); }} placeholder="Confirm your email address" error={errors.confirmEmail} />
            )}

            <Field label="PASSWORD" type={showPassword ? 'text' : 'password'} value={password}
              onChange={e => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: '' })); }}
              placeholder={mode === 'register' ? 'Minimum 8 characters' : 'Enter your password'}
              error={errors.password}
              hint={mode === 'register' && password ? pwStrength.label : null}
            >
              <button type="button" onClick={() => setShowPassword(s => !s)}
                style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0 }}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </Field>

            {/* Password strength bar */}
            {mode === 'register' && password && (
              <div style={{ marginTop: '-0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(pwStrength.score / 4) * 100}%`, background: pwStrength.color, borderRadius: '2px', transition: 'all 0.3s' }} />
                </div>
              </div>
            )}

            {mode === 'register' && (
              <Field label="CONFIRM PASSWORD" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
                placeholder="Repeat your password" error={errors.confirmPassword}
              >
                <button type="button" onClick={() => setShowConfirmPassword(s => !s)}
                  style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0 }}>
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </Field>
            )}

            {mode === 'register' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div onClick={() => setAgreed(a => !a)} style={{ width: 18, height: 18, borderRadius: '5px', border: `2px solid ${errors.agreed ? '#ff4444' : agreed ? '#00ff88' : 'rgba(255,255,255,0.2)'}`, background: agreed ? 'rgba(0,255,136,0.15)' : 'transparent', flexShrink: 0, marginTop: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', cursor: 'pointer' }}>
                    {agreed && <CheckCircle size={12} style={{ color: '#00ff88' }} />}
                  </div>
                  <span style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
                    I agree to MarketPulze's <button type="button" onClick={() => onNavigate && onNavigate('terms', { mode, email, confirmEmail, password, confirmPassword, agreed })} style={{ background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'inherit' }}>Terms of Service</button> and <button type="button" onClick={() => onNavigate && onNavigate('privacy', { mode, email, confirmEmail, password, confirmPassword, agreed })} style={{ background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'inherit' }}>Privacy Policy</button>. I understand this platform provides informational analysis only, not financial advice.
                  </span>
                </div>
                {errors.agreed && <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.4rem' }}><AlertCircle size={12} style={{ color: '#ff4444' }} /><span style={{ fontSize: '0.75rem', color: '#ff4444' }}>{errors.agreed}</span></div>}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '0.95rem', background: loading ? 'rgba(0,255,136,0.3)' : 'linear-gradient(135deg,#00ff88,#00d4ff)',
              border: 'none', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.95rem',
              cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '0.02em', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'opacity 0.2s',
            }}>
              {loading ? (
                <><span style={{ width: 16, height: 16, border: '2px solid rgba(5,13,26,0.3)', borderTop: '2px solid #050d1a', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />Processing...</>
              ) : (
                <>{mode === 'login' ? 'Sign In' : 'Create Account'} <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          {mode === 'login' && (
            <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
              <button onClick={() => { setMode('forgot'); setErrors({}); setGlobalError(''); }} style={styles.linkBtn}>Forgot your password?</button>
            </div>
          )}

          {mode === 'forgot' && (
            <ForgotPasswordForm
              email={email}
              setEmail={setEmail}
              apiUrl={API_URL}
              onBack={() => { setMode('login'); setErrors({}); setGlobalError(''); }}
            />
          )}

          {mode !== 'forgot' && (
          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: '0.85rem', color: '#475569' }}>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button onClick={switchMode} style={{ ...styles.linkBtn, fontWeight: 700 }}>
                {mode === 'login' ? 'Sign up free' : 'Sign in'}
              </button>
            </span>
          </div>
          )}

          <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#334155', marginTop: '2rem', lineHeight: 1.6 }}>
            MarketPulze is for informational purposes only. Not financial advice.<br />Not a registered investment advisor.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #334155; }
        input::-webkit-input-placeholder { color: #334155; }
      `}</style>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  pageWrap: {
    minHeight: '100vh', display: 'flex', background: '#050d1a',
    fontFamily: '"DM Sans", system-ui, sans-serif',
  },
  leftPanel: {
    flex: '1 1 55%', background: 'linear-gradient(160deg, rgba(10,20,45,0.95) 0%, rgba(5,13,26,0.98) 100%)',
    borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column',
    position: 'relative', overflow: 'hidden',
    '@media (max-width: 768px)': { display: 'none' },
  },
  rightPanel: {
    flex: '1 1 45%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '3rem 2rem', position: 'relative', minWidth: 0,
  },
  tickerWrap: {
    borderBottom: '1px solid rgba(255,255,255,0.04)', padding: '0.6rem 0',
    overflow: 'hidden', background: 'rgba(0,0,0,0.2)',
  },
  verifyCard: {
    maxWidth: '480px', width: '100%', margin: '0 auto', padding: '3rem 2rem',
    textAlign: 'center', background: 'rgba(10,20,45,0.6)', borderRadius: '20px',
    border: '1px solid rgba(0,212,255,0.15)',
  },
  btnSecondary: {
    padding: '0.85rem 2rem', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)',
    borderRadius: '10px', color: '#00d4ff', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
    fontFamily: 'inherit', width: '100%',
  },
  linkBtn: {
    background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer',
    textDecoration: 'none', fontSize: 'inherit', fontFamily: 'inherit', padding: 0,
  },
};

export default AuthPage;
